import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as profileService from '../services/profileService';
import type {
  UpdateAvatarData,
  UpdateProfileData,
  UploadAvatarData,
  DeleteAvatarData,
  UpdatePasswordData,
  CreateMessageTemplateData,
  UpdateMessageTemplateData,
  UpdateAiSummaryData,
} from '../types/profile';
import { PROFILE_KEY, AUTH_KEY } from '../utils/query-keys';

export const useGetProfile = () =>
  useQuery({
    queryKey: [...PROFILE_KEY, 'fullProfile'],
    queryFn: profileService.getProfile,
  });

export const useAiSummary = () =>
  useQuery({
    queryKey: [...PROFILE_KEY, 'aiSummary'],
    queryFn: profileService.getAiSummary,
    // Poll fallback so a missed `summary.ready` socket event can't wedge the
    // onboarding AI-summary page on its loader forever (the page otherwise only
    // refetches on that socket event). Polls every 4s until the persona webhook
    // has fired (summary ready), then stops. Belt-and-suspenders on top of the
    // socket; harmless once the page has rendered.
    refetchInterval: query =>
      query.state.data?.result?.webhook ? false : 4000,
  });

export const useUpdateAiSummary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateAiSummaryData) => profileService.updateAiSummary(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROFILE_KEY, 'aiSummary'] });
    },
  });
};

export const useUpdateProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProfileData) => profileService.updateProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...AUTH_KEY, 'profile'] });
    },
  });
};

export const useUploadAvatar = () => {
  return useMutation({
    mutationFn: (data: UploadAvatarData) => profileService.uploadAvatar(data),
  });
};

export const useUpdateAvatar = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateAvatarData) => profileService.updateAvatar(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...AUTH_KEY, 'profile'] });
    },
  });
};

export const useDeleteAvatar = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DeleteAvatarData) => profileService.deleteAvatar(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...AUTH_KEY, 'profile'] });
    },
  });
};

export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: (data: UpdatePasswordData) =>
      profileService.updatePassword(data),
  });
};

export const useGetMessageTemplates = () =>
  useQuery({
    queryKey: [...PROFILE_KEY, 'messageTemplates'],
    queryFn: profileService.getMessageTemplates,
  });

export const useCreateMessageTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMessageTemplateData) =>
      profileService.createMessageTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROFILE_KEY, 'messageTemplates'] });
    },
  });
};

export const useUpdateMessageTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMessageTemplateData) =>
      profileService.updateMessageTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROFILE_KEY, 'messageTemplates'] });
    },
  });
};
